import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Stack,
  Divider,
  Alert,
  Chip,
  IconButton,
  InputAdornment,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Slider,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  alpha
} from '@mui/material';
import {
  AttachMoney as CostIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Calculate as CalculateIcon,
  TrendingUp as TrendingUpIcon,
  ExpandMore as ExpandMoreIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as SupplierIcon,
  Factory as FactoryIcon,
  Business as BusinessIcon,
  Info as InfoIcon,
  Assessment as AssessmentIcon,
  Remove as RemoveIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { supabase } from '../../../supabaseClient';
import { usePermissions } from '../../../utils/permissions';
import { useNavigate } from 'react-router-dom';
import {
  convertUnits,
  getUnitType,
  calculateRawMaterialCost,
  WEIGHT_UNITS,
  VOLUME_UNITS
} from '../../../utils/unitConversions';

const ProductionLineCosts = ({ 
  lineProduction, 
  showSnackbar, 
  userCompanyId, 
  onUpdate, 
  user, 
  onSave 
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Materials data
  const [materialItems, setMaterialItems] = useState([]);
  const [linkedBatch, setLinkedBatch] = useState(null);
  const [productDetails, setProductDetails] = useState(null);
  const [unitConfigurations, setUnitConfigurations] = useState([]);
  
  // Cost parameters
  const [laborPercentage, setLaborPercentage] = useState(40);
  
  // Global overhead percentage
  const [globalOverheadPercentage, setGlobalOverheadPercentage] = useState(28);
  
  // Overhead categories as % of the overhead budget (should sum to 100%)
  const [utilitiesPercentage, setUtilitiesPercentage] = useState(10);        
  const [facilityPercentage, setFacilityPercentage] = useState(25);          
  const [equipmentPercentage, setEquipmentPercentage] = useState(15);        
  const [adminLaborPercentage, setAdminLaborPercentage] = useState(18);      
  const [warehousePercentage, setWarehousePercentage] = useState(7);         
  const [qualityPercentage, setQualityPercentage] = useState(10);            
  const [softwarePercentage, setSoftwarePercentage] = useState(8);           
  const [insurancePercentage, setInsurancePercentage] = useState(7);         
  
  // Additional costs
  const [shippingCost, setShippingCost] = useState(0);
  const [wasteCost, setWasteCost] = useState(0);
  const [reworkCost, setReworkCost] = useState(0);
  const [miscCost, setMiscCost] = useState(0);
  const [marketingCostPerUnit, setMarketingCostPerUnit] = useState(0);
  
  // Settings
  const [costsInputted, setCostsInputted] = useState(false);
  
  // Calculated costs
  const [costs, setCosts] = useState({
    totalMaterialCost: 0,
    laborCost: 0,
    overheadCost: 0,
    additionalCost: 0,
    marketingCost: 0,
    totalCost: 0,
    costPerUnit: 0,
    materialCosts: []
  });

  // Accordion state management with localStorage persistence
  const [accordionStates, setAccordionStates] = useState({
    costSummary: false,
    materialBreakdown: false,
    laborCost: false,
    marketingCosts: false,
    overheadCosts: false,
    additionalCosts: false
  });

  const allUnits = [...WEIGHT_UNITS, ...VOLUME_UNITS];

  // Fetch unit configurations
  const fetchUnitConfigurations = async () => {
    try {
      const { data, error } = await supabase
        .from('unit_configurations')
        .select('*')
        .eq('company_id', userCompanyId)
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      setUnitConfigurations(data || []);
    } catch (err) {
      console.error('Error fetching unit configurations:', err);
    }
  };

  // Calculate total units from unit configuration like ProductionLineOverview.jsx
  const calculateTotalUnitsFromLineProduction = () => {
    if (!lineProduction) return 0;

    // Use unit configuration if available
    if (lineProduction.unit_configuration_id) {
      const unitConfig = unitConfigurations.find(config => config.id === lineProduction.unit_configuration_id);
      if (unitConfig) {
        let totalUnits = 0;
        const pallets = lineProduction.pallet_quantity || 0;
        const cases = lineProduction.case_quantity || 0;
        const units = lineProduction.unit_quantity || 0;
        
        // Add units from pallets (only if both pallets and cases are enabled)
        if (unitConfig.enable_pallets && unitConfig.enable_cases) {
          totalUnits += pallets * unitConfig.cases_per_pallet * unitConfig.units_per_case;
        }
        
        // Add units from cases (independent cases, not from pallets)
        if (unitConfig.enable_cases) {
          totalUnits += cases * unitConfig.units_per_case;
        }
        
        // Add individual units
        totalUnits += units;
        
        return totalUnits;
      }
    }
    
    // Fallback to variant distribution or planned quantity
    if (lineProduction.variant_distribution && lineProduction.variant_distribution.length > 0) {
      return lineProduction.variant_distribution.reduce((sum, variant) => sum + variant.quantity, 0);
    }
    
    return lineProduction.planned_quantity || 0;
  };
  
  // Calculate distribution total (should add up to 100% for proper distribution)
  const distributionTotal = utilitiesPercentage + facilityPercentage + equipmentPercentage + 
                           adminLaborPercentage + warehousePercentage + qualityPercentage + 
                           softwarePercentage + insurancePercentage;

  // Format currency
  const formatCurrency = (amount) => `$${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  
  // Format cost per unit with more decimal places for small amounts
  const formatCostPerUnit = (amount) => {
    if (!amount || amount === 0) return '$0.00';
    if (amount < 0.01) {
      return `$${amount.toFixed(4)}`;
    }
    return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Load accordion states from localStorage on mount
  useEffect(() => {
    const savedStates = localStorage.getItem(`productionLineCosts_accordions_${lineProduction?.id || 'default'}`);
    if (savedStates) {
      try {
        setAccordionStates(JSON.parse(savedStates));
      } catch (error) {
        console.warn('Failed to parse saved accordion states:', error);
      }
    }
  }, [lineProduction?.id]);

  // Save accordion states to localStorage when they change
  const updateAccordionState = (accordionKey, expanded) => {
    const newStates = { ...accordionStates, [accordionKey]: expanded };
    setAccordionStates(newStates);
    localStorage.setItem(`productionLineCosts_accordions_${lineProduction?.id || 'default'}`, JSON.stringify(newStates));
  };

  // Function declarations - must be before useEffect hooks
  const loadMaterialItems = async () => {
    try {
      // Load production line items without the problematic joins
      const { data, error } = await supabase
        .from('production_line_items')
        .select('*')
        .eq('line_production_id', lineProduction.id);

      if (error) throw error;
      setMaterialItems(data || []);
    } catch (error) {
      console.error('Error loading material items:', error);
    }
  };

  const loadLinkedBatch = async () => {
    if (!lineProduction?.batch_id) return;
    
    try {
      const { data, error } = await supabase
        .from('production_batches')
        .select(`
          *,
          batch_costs (total_cost)
        `)
        .eq('id', lineProduction.batch_id)
        .single();

      if (error) throw error;
      setLinkedBatch(data);
    } catch (error) {
      console.error('Error loading linked batch:', error);
    }
  };

  const loadProductDetails = async () => {
    if (!lineProduction?.product_id) return;
    
    try {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, volume_unit, volume_weight')
        .eq('id', lineProduction.product_id)
        .single();

      if (error) throw error;
      setProductDetails(data);
    } catch (error) {
      console.error('Error loading product details:', error);
    }
  };

  const loadProductionLineCosts = async () => {
    try {
      const { data, error } = await supabase
        .from('production_line_costs')
        .select('*')
        .eq('line_production_id', lineProduction.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116' && error.code !== 'PGRST200') {
        console.error('Error loading production line costs:', error);
        return;
      }
      
      if (data) {
        setLaborPercentage(data.labor_percentage ?? 40);
        setGlobalOverheadPercentage(data.global_overhead_percentage ?? 28);
        setUtilitiesPercentage(data.utilities_percentage ?? 10);
        setFacilityPercentage(data.facility_percentage ?? 25);
        setEquipmentPercentage(data.equipment_percentage ?? 15);
        setAdminLaborPercentage(data.admin_labor_percentage ?? 18);
        setWarehousePercentage(data.warehouse_percentage ?? 7);
        setQualityPercentage(data.quality_percentage ?? 10);
        setSoftwarePercentage(data.software_percentage ?? 8);
        setInsurancePercentage(data.insurance_percentage ?? 7);
        setShippingCost(data.shipping_cost ?? 0);
        setWasteCost(data.waste_cost ?? 0);
        setReworkCost(data.rework_cost ?? 0);
        setMiscCost(data.misc_cost ?? 0);
        setMarketingCostPerUnit(data.marketing_cost_per_unit ?? 0);
        setCostsInputted(data.is_completed ?? false);
      }
    } catch (error) {
      console.error('Error loading production line costs:', error);
      // Don't throw error, just continue with defaults
    }
  };

  const calculateMaterialCosts = async () => {
    if (!lineProduction?.id) return;

    try {
      // Load actual production line items with their inventory details
      const { data: lineItems, error: lineItemsError } = await supabase
        .from('production_line_items')
        .select('*')
        .eq('line_production_id', lineProduction.id);

      if (lineItemsError) throw lineItemsError;

      const materialCosts = [];
      let totalMaterialCost = 0;

      // Process each line item to get actual costs from inventory tables
      for (const item of lineItems) {
        if (!item.actual_quantity || item.actual_quantity <= 0) continue;

        let itemDetails = null;
        let unitCost = 0;
        let itemName = 'Unknown Item';
        let itemType = item.item_type?.toUpperCase() || 'UNKNOWN';

        try {
          if (item.item_type === 'bottle') {
            const { data: bottleData } = await supabase
              .from('bottles')
              .select('id, name, sku, unit_cost')
              .eq('id', item.item_id)
              .single();
            if (bottleData) {
              itemDetails = bottleData;
              unitCost = bottleData.unit_cost || 0;
              itemName = bottleData.name;
              itemType = 'BOTTLE';
            }
          } else if (item.item_type === 'cap') {
            const { data: capData } = await supabase
              .from('caps')
              .select('id, name, sku, unit_cost')
              .eq('id', item.item_id)
              .single();
            if (capData) {
              itemDetails = capData;
              unitCost = capData.unit_cost || 0;
              itemName = capData.name;
              itemType = 'CAP';
            }
          } else if (item.item_type === 'label') {
            const { data: labelData } = await supabase
              .from('labels')
              .select('id, name, sku, unit_cost')
              .eq('id', item.item_id)
              .single();
            if (labelData) {
              itemDetails = labelData;
              unitCost = labelData.unit_cost || 0;
              itemName = labelData.name;
              itemType = 'LABEL';
            }
          } else if (item.item_type === 'supply') {
            const { data: supplyData } = await supabase
              .from('supplies')
              .select('id, name, sku, unit_cost')
              .eq('id', item.item_id)
              .single();
            if (supplyData) {
              itemDetails = supplyData;
              unitCost = supplyData.unit_cost || 0;
              itemName = supplyData.name;
              itemType = 'SUPPLY';
            }
          }
        } catch (detailError) {
          console.error(`Error loading ${item.item_type} details:`, detailError);
        }

        const totalCost = item.actual_quantity * unitCost;
        
        materialCosts.push({
          material_id: item.item_id,
          material_name: itemName,
          material_type: itemType,
          total_cost: totalCost,
          has_pricing: unitCost > 0,
          quantity: item.actual_quantity,
          unit: item.actual_unit || item.planned_unit || 'units'
        });
        
        totalMaterialCost += totalCost;
      }

      // Add batch production cost if linked (check both sources)
      let batchCost = 0;
      if (lineProduction?.batch_id) {
        // Try to get cost from linked batch in lineProduction first
        if (lineProduction.batch_total_material_cost) {
          batchCost = lineProduction.batch_total_material_cost;
        } else if (linkedBatch?.batch_costs?.[0]?.total_cost) {
          batchCost = linkedBatch.batch_costs[0].total_cost;
        }
        
        if (batchCost > 0) {
          totalMaterialCost += batchCost;
          
          materialCosts.push({
            material_id: 'batch_production',
            material_name: 'Batch Production Cost',
            material_type: 'BATCH',
            total_cost: batchCost,
            has_pricing: true,
            quantity: 1,
            unit: 'batch'
          });
        }
      }

      const laborCost = totalMaterialCost * (laborPercentage / 100);
      const overheadCost = totalMaterialCost * (globalOverheadPercentage / 100);
      
      // Calculate marketing cost based on properly calculated units
      const lineQuantity = calculateTotalUnitsFromLineProduction();
      const marketingTotalCost = marketingCostPerUnit * lineQuantity;
      
      const additionalCost = shippingCost + wasteCost + reworkCost + miscCost;
      const totalCost = totalMaterialCost + laborCost + overheadCost + additionalCost + marketingTotalCost;
      
      // Calculate cost per unit (simple division - no unit conversion)
      let costPerUnit = 0;
      
      if (lineQuantity > 0 && totalCost > 0) {
        costPerUnit = totalCost / lineQuantity;
      }

      setCosts({
        totalMaterialCost,
        laborCost,
        overheadCost,
        additionalCost,
        marketingCost: marketingTotalCost,
        totalCost,
        costPerUnit,
        materialCosts
      });

    } catch (error) {
      console.error('Error calculating material costs:', error);
      // Set empty costs on error
      setCosts({
        totalMaterialCost: 0,
        laborCost: 0,
        overheadCost: 0,
        additionalCost: 0,
        marketingCost: 0,
        totalCost: 0,
        costPerUnit: 0,
        materialCosts: []
      });
    }
  };

  const saveCosts = async () => {
    // Check permissions before proceeding
    if (!canEdit) {
      showSnackbar('You do not have permission to save production line costs.', 'error');
      return;
    }

    setSaving(true);
    try {
      const { data: existingCosts } = await supabase
        .from('production_line_costs')
        .select('id')
        .eq('line_production_id', lineProduction.id)
        .maybeSingle();

      const costDataToSave = {
        line_production_id: lineProduction.id,
        labor_percentage: laborPercentage,
        global_overhead_percentage: globalOverheadPercentage,
        utilities_percentage: utilitiesPercentage,
        facility_percentage: facilityPercentage,
        equipment_percentage: equipmentPercentage,
        admin_labor_percentage: adminLaborPercentage,
        warehouse_percentage: warehousePercentage,
        quality_percentage: qualityPercentage,
        software_percentage: softwarePercentage,
        insurance_percentage: insurancePercentage,
        shipping_cost: shippingCost,
        waste_cost: wasteCost,
        rework_cost: reworkCost,
        misc_cost: miscCost,
        marketing_cost_per_unit: marketingCostPerUnit,
        total_material_cost: costs.totalMaterialCost,
        labor_cost: costs.laborCost,
        overhead_cost: costs.overheadCost,
        additional_cost: costs.additionalCost,
        marketing_cost: costs.marketingCost,
        total_cost: costs.totalCost,
        cost_per_unit: costs.costPerUnit,
        total_production_batch_cost: costs.totalCost,
        calculated_overhead_cost: costs.overheadCost,
        is_completed: true,
        completed_at: new Date().toISOString(),
        completed_by: user?.id,
        updated_by: user?.id
      };

      if (existingCosts) {
        const { error } = await supabase
          .from('production_line_costs')
          .update(costDataToSave)
          .eq('id', existingCosts.id);
        
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('production_line_costs')
          .insert([{
            ...costDataToSave,
            created_by: user?.id
          }]);
        
        if (error) throw error;
      }

      // Update the main line production to mark cost analysis step as completed
      const { error: lineProductionError } = await supabase
        .from('line_productions')
        .update({
          steps_completed: {
            ...lineProduction.steps_completed,
            cost_analysis: {
              completed: true,
              completed_at: new Date().toISOString(),
              completed_by: user?.id
            }
          },
          updated_by: user?.id
        })
        .eq('id', lineProduction.id);

      if (lineProductionError) throw lineProductionError;

      setCostsInputted(true);
      showSnackbar('Production line costs saved successfully!', 'success');
      onUpdate();
      if (onSave) onSave(); // Mark cost analysis as complete
    } catch (error) {
      console.error('Error saving production line costs:', error);
      showSnackbar('Error saving production line costs: ' + error.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (lineProduction?.id) {
      loadMaterialItems();
      loadLinkedBatch();
      loadProductionLineCosts();
      loadProductDetails();
      fetchUnitConfigurations();
    }
  }, [lineProduction?.id]);


  useEffect(() => {
    if (lineProduction?.id) {
      calculateMaterialCosts();
    }
  }, [lineProduction?.id, linkedBatch, laborPercentage, globalOverheadPercentage,
      utilitiesPercentage, facilityPercentage, equipmentPercentage, adminLaborPercentage,
      warehousePercentage, qualityPercentage, softwarePercentage, insurancePercentage,
      shippingCost, wasteCost, reworkCost, miscCost, marketingCostPerUnit, unitConfigurations]);

  // Permission check - must be after all hooks
  if (!hasPermission('production.line.assembly.cost.view', 'read')) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: 'calc(100vh - 200px)',
        textAlign: 'center',
        width: '100%',
        px: 3,
        py: 8
      }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <CostIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Production Line Costs Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access Production Line Costs. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
      </Box>
    );
  }

  // Check edit permissions  
  const canEdit = hasPermission('production.line.assembly.cost.edit', 'write');

  const hasNoPricing = costs.materialCosts?.some(item => !item.has_pricing);

  return (
    <Box sx={{ 
      p: 2,
      pl: 3,
      pr: 3,
      maxWidth: '98vw',
      width: '100%'
    }}>
      <Grid container spacing={3}>
        {/* Panel A: Cost Overview */}
        <Grid item xs={12} lg={3.5}>
          <Card sx={{ 
            height: 'fit-content',
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: `white`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Stack spacing={3}>
                {/* Header */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ 
                      p: 1.5, 
                      borderRadius: 2, 
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      backdropFilter: 'blur(10px)'
                    }}>
                      <CostIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                    </Box>
                    <Box sx={{ ml: 2 }}>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                        Cost Analysis
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        Production costs
                      </Typography>
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={2}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={() => {
                        loadMaterialItems();
                        loadLinkedBatch();
                        loadProductionLineCosts();
                      }}
                      sx={{
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                        background: theme.palette.grey[400],
                        border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                        backdropFilter: 'blur(10px)',
                        '&:hover': {
                          backgroundColor: theme.palette.grey[400]
                        }
                      }}
                    >
                      Refresh
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                      onClick={saveCosts}
                      disabled={saving || !canEdit}
                      sx={{
                        borderRadius: 2,
                        px: 4,
                        py: 1.5,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        color: 'white',
                        fontWeight: 500,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                        '&:hover': {
                          background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                          boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                        },
                        '&:disabled': {
                          background: theme.palette.grey[400],
                          color: theme.palette.grey[100]
                        }
                      }}
                    >
                      Save
                    </Button>
                  </Stack>
                </Box>

                {/* Total Cost Display */}
                <Accordion 
                  expanded={accordionStates.costSummary}
                  onChange={(event, expanded) => updateAccordionState('costSummary', expanded)}
                  elevation={0}
                  sx={{
                    borderRadius: '12px !important',
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(20px)',
                    overflow: 'hidden',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                    '&:before': {
                      display: 'none'
                    },
                    '&.Mui-expanded': {
                      margin: '16px 0 16px 0',
                      borderRadius: '12px !important'
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '& .MuiAccordion-region': {
                      borderRadius: '0 0 12px 12px !important'
                    }
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                    sx={{
                      background: 'white',
                      backdropFilter: 'blur(10px)',
                      '& .MuiAccordionSummary-content': { alignItems: 'center', my: 2 },
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.04)
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <AssessmentIcon sx={{ color: theme.palette.primary.main }} />
                      <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                        Cost Summary
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Box sx={{ 
                        p: 2,
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                        backdropFilter: 'blur(10px)',
                        textAlign: 'center',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                        }
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          TOTAL COST {(lineProduction?.batch_id || linkedBatch) ? '(WITH BATCH)' : '(NO BATCH)'}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 500, color: '#000000' }}>
                          {formatCurrency(costs.totalCost)}
                        </Typography>
                      </Box>

                      <Box sx={{ 
                        p: 2,
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                        backdropFilter: 'blur(10px)',
                        textAlign: 'center',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                        }
                      }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                          COST PER UNIT {(lineProduction?.batch_id || linkedBatch) ? '(WITH BATCH)' : '(NO BATCH)'}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 500, color: '#000000' }}>
                          {formatCostPerUnit(costs.costPerUnit)}
                        </Typography>
                      </Box>
                    </Stack>
                  </AccordionDetails>
                </Accordion>


                {/* Alerts */}
                {hasNoPricing && (
                  <Alert 
                    severity="warning" 
                    sx={{ 
                      fontSize: '0.8rem',
                      borderRadius: 2,
                      border: '1px solid #ff9800',
                      fontWeight: 500
                    }}
                  >
                    Some materials missing pricing data
                  </Alert>
                )}

                {!costsInputted && (
                  <Alert 
                    severity="info" 
                    sx={{ 
                      fontSize: '0.8rem',
                      borderRadius: 2,
                      border: '1px solid #2196f3',
                      fontWeight: 500
                    }}
                  >
                    Configure cost parameters to get accurate analysis
                  </Alert>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Panel B: Cost Configuration */}
        <Grid item xs={12} lg={5.5}>
          <Card sx={{ 
            height: 'fit-content',
            borderRadius: 4,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: `white`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <CardContent sx={{ p: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 3 }}>
                Cost Configuration
              </Typography>

              <Stack spacing={2}>
                {/* Material Breakdown - Placeholder */}
                <Accordion 
                  expanded={accordionStates.materialBreakdown}
                  onChange={(event, expanded) => updateAccordionState('materialBreakdown', expanded)}
                  elevation={0}
                  sx={{
                    borderRadius: '12px !important',
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(20px)',
                    overflow: 'hidden',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                    '&:before': {
                      display: 'none'
                    },
                    '&.Mui-expanded': {
                      margin: '8px 0 8px 0',
                      borderRadius: '12px !important'
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '& .MuiAccordion-region': {
                      borderRadius: '0 0 12px 12px !important'
                    }
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                    sx={{
                      background: 'white',
                      backdropFilter: 'blur(10px)',
                      minHeight: 56,
                      '& .MuiAccordionSummary-content': { alignItems: 'center', my: 2 },
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.04)
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                      <InfoIcon sx={{ color: theme.palette.primary.main }} />
                      <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                        Material Breakdown
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                        {formatCurrency(costs.totalMaterialCost)}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      {/* Material Cost List */}
                      {costs.materialCosts && costs.materialCosts.length > 0 ? (
                        <>
                          {/* Caps */}
                          {costs.materialCosts.filter(item => item.material_type === 'CAP').map((item, index) => (
                            <Box
                              key={`cap-${index}`}
                              sx={{ 
                                p: 2,
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.grey[400]}`,
                                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                                backdropFilter: 'blur(10px)',
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                                }
                              }}
                            >
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                  <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                                    {item.material_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {item.quantity.toLocaleString()} {item.unit} used
                                  </Typography>
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                                  {formatCurrency(item.total_cost)}
                                </Typography>
                              </Stack>
                              {!item.has_pricing && (
                                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                                  No pricing data available
                                </Typography>
                              )}
                            </Box>
                          ))}

                          {/* Bottles */}
                          {costs.materialCosts.filter(item => item.material_type === 'BOTTLE').map((item, index) => (
                            <Box
                              key={`bottle-${index}`}
                              sx={{ 
                                p: 2,
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.grey[400]}`,
                                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                                backdropFilter: 'blur(10px)',
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                                }
                              }}
                            >
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                  <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                                    {item.material_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {item.quantity.toLocaleString()} {item.unit} used
                                  </Typography>
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                                  {formatCurrency(item.total_cost)}
                                </Typography>
                              </Stack>
                              {!item.has_pricing && (
                                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                                  No pricing data available
                                </Typography>
                              )}
                            </Box>
                          ))}

                          {/* Labels */}
                          {costs.materialCosts.filter(item => item.material_type === 'LABEL').map((item, index) => (
                            <Box
                              key={`label-${index}`}
                              sx={{ 
                                p: 2,
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.grey[400]}`,
                                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                                backdropFilter: 'blur(10px)',
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                                }
                              }}
                            >
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                  <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                                    {item.material_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {item.quantity.toLocaleString()} {item.unit} used
                                  </Typography>
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                                  {formatCurrency(item.total_cost)}
                                </Typography>
                              </Stack>
                              {!item.has_pricing && (
                                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                                  No pricing data available
                                </Typography>
                              )}
                            </Box>
                          ))}

                          {/* Supplies */}
                          {costs.materialCosts.filter(item => item.material_type === 'SUPPLY').map((item, index) => (
                            <Box
                              key={`supply-${index}`}
                              sx={{ 
                                p: 2,
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.grey[400]}`,
                                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                                backdropFilter: 'blur(10px)',
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                                }
                              }}
                            >
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                  <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                                    {item.material_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {item.quantity.toLocaleString()} {item.unit} used
                                  </Typography>
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                                  {formatCurrency(item.total_cost)}
                                </Typography>
                              </Stack>
                              {!item.has_pricing && (
                                <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                                  No pricing data available
                                </Typography>
                              )}
                            </Box>
                          ))}

                          {/* Batch Production Cost */}
                          {costs.materialCosts.filter(item => item.material_type === 'BATCH').map((item, index) => (
                            <Box
                              key={`batch-${index}`}
                              sx={{ 
                                p: 2,
                                borderRadius: 3,
                                border: `1px solid ${theme.palette.grey[400]}`,
                                background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                                backdropFilter: 'blur(10px)',
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                                }
                              }}
                            >
                              <Stack direction="row" justifyContent="space-between" alignItems="center">
                                <Box>
                                  <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                                    {item.material_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {item.quantity.toLocaleString()} {item.unit} used
                                  </Typography>
                                </Box>
                                <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                                  {formatCurrency(item.total_cost)}
                                </Typography>
                              </Stack>
                            </Box>
                          ))}

                          {/* Total */}
                          <Divider />
                          <Box
                            sx={{ 
                              p: 3,
                              borderRadius: 3,
                              border: `1px solid ${theme.palette.primary.main}`,
                              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                              backdropFilter: 'blur(10px)',
                              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                              }
                            }}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                                Total Material Cost
                              </Typography>
                              <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                                {formatCurrency(costs.totalMaterialCost)}
                              </Typography>
                            </Stack>
                          </Box>
                        </>
                      ) : (
                        <Box sx={{ 
                          p: 4,
                          borderRadius: 3,
                          border: `1px solid ${theme.palette.grey[400]}`,
                          background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                          backdropFilter: 'blur(10px)',
                          textAlign: 'center',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                          }
                        }}>
                          <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 2 }}>
                            No Material Costs Available
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            Generate line items and set lot/batch selections to see material breakdown
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                {/* Marketing Costs */}
                <Accordion 
                  expanded={accordionStates.marketingCosts}
                  onChange={(event, expanded) => updateAccordionState('marketingCosts', expanded)}
                  elevation={0}
                  sx={{
                    borderRadius: '12px !important',
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(20px)',
                    overflow: 'hidden',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                    '&:before': {
                      display: 'none'
                    },
                    '&.Mui-expanded': {
                      margin: '8px 0 8px 0',
                      borderRadius: '12px !important'
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '& .MuiAccordion-region': {
                      borderRadius: '0 0 12px 12px !important'
                    }
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                    sx={{
                      background: 'white',
                      backdropFilter: 'blur(10px)',
                      minHeight: 56,
                      '& .MuiAccordionSummary-content': { alignItems: 'center', my: 2 },
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.04)
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                      <TrendingUpIcon sx={{ color: theme.palette.primary.main }} />
                      <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                        Marketing Costs
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                        {formatCurrency(marketingCostPerUnit * calculateTotalUnitsFromLineProduction())}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2 }}>
                    <Box sx={{ 
                      p: 2,
                      borderRadius: 3,
                      border: `1px solid ${theme.palette.grey[400]}`,
                      background: 'white',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                      }
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 2, color: '#000000' }}>
                        Marketing Cost Per Unit
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={6}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Cost Per Unit"
                            type="number"
                            value={marketingCostPerUnit}
                            onChange={(e) => canEdit ? setMarketingCostPerUnit(Math.max(parseFloat(e.target.value) || 0, 0)) : null}
                            disabled={!canEdit}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                '& fieldset': { 
                                  borderColor: alpha(theme.palette.grey[400], 0.8), 
                                  borderWidth: 1,
                                  borderRadius: 2
                                },
                                '&:hover fieldset': { 
                                  borderColor: alpha(theme.palette.primary.main, 0.4), 
                                  borderWidth: 1 
                                },
                                '&.Mui-focused fieldset': { 
                                  borderColor: theme.palette.primary.main, 
                                  borderWidth: 1 
                                }
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                            × {calculateTotalUnitsFromLineProduction().toLocaleString()} units
                          </Typography>
                        </Grid>
                        <Grid item xs={3}>
                          <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                            {formatCurrency(marketingCostPerUnit * calculateTotalUnitsFromLineProduction())}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                        Marketing budget allocated per product unit sold
                      </Typography>
                    </Box>
                  </AccordionDetails>
                </Accordion>

                {/* Labor Cost */}
                <Accordion 
                  expanded={accordionStates.laborCost}
                  onChange={(event, expanded) => updateAccordionState('laborCost', expanded)}
                  elevation={0}
                  sx={{
                    borderRadius: '12px !important',
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(20px)',
                    overflow: 'hidden',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                    '&:before': {
                      display: 'none'
                    },
                    '&.Mui-expanded': {
                      margin: '8px 0 8px 0',
                      borderRadius: '12px !important'
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '& .MuiAccordion-region': {
                      borderRadius: '0 0 12px 12px !important'
                    }
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                    sx={{
                      background: 'white',
                      backdropFilter: 'blur(10px)',
                      minHeight: 56,
                      '& .MuiAccordionSummary-content': { alignItems: 'center', my: 2 },
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.04)
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                      <TrendingUpIcon sx={{ color: theme.palette.primary.main }} />
                      <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                        Labor Cost
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                        {formatCurrency(costs.laborCost)}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Box>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                          Labor as % of material costs
                        </Typography>
                        <Slider
                          value={laborPercentage}
                          onChange={(e, newValue) => canEdit ? setLaborPercentage(newValue) : null}
                          disabled={!canEdit}
                          valueLabelDisplay="auto"
                          step={5}
                          min={0}
                          max={100}
                          marks={[
                            { value: 0, label: '0%' },
                            { value: 50, label: '50%' },
                            { value: 100, label: '100%' }
                          ]}
                          sx={{
                            '& .MuiSlider-thumb': {
                              backgroundColor: theme.palette.primary.main
                            },
                            '& .MuiSlider-track': {
                              backgroundColor: theme.palette.primary.main
                            },
                            '& .MuiSlider-markLabel': {
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              color: 'text.primary',
                              '&[data-index="0"]': {
                                transform: 'translateX(0%)'
                              },
                              '&[data-index="2"]': {
                                transform: 'translateX(-100%)'
                              }
                            }
                          }}
                        />
                      </Box>
                      <TextField
                        size="small"
                        label="Labor Percentage"
                        type="number"
                        value={laborPercentage}
                        onChange={(e) => canEdit ? setLaborPercentage(Math.min(Math.max(parseInt(e.target.value) || 0, 0), 100)) : null}
                        disabled={!canEdit}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { 
                              borderColor: alpha(theme.palette.grey[400], 0.8), 
                              borderWidth: 1,
                              borderRadius: 2
                            },
                            '&:hover fieldset': { 
                              borderColor: alpha(theme.palette.primary.main, 0.4), 
                              borderWidth: 1 
                            },
                            '&.Mui-focused fieldset': { 
                              borderColor: theme.palette.primary.main, 
                              borderWidth: 1 
                            }
                          }
                        }}
                      />
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                {/* Overhead Costs */}
                <Accordion 
                  expanded={accordionStates.overheadCosts}
                  onChange={(event, expanded) => updateAccordionState('overheadCosts', expanded)}
                  elevation={0}
                  sx={{
                    borderRadius: '12px !important',
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(20px)',
                    overflow: 'hidden',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                    '&:before': {
                      display: 'none'
                    },
                    '&.Mui-expanded': {
                      margin: '8px 0 8px 0',
                      borderRadius: '12px !important'
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '& .MuiAccordion-region': {
                      borderRadius: '0 0 12px 12px !important'
                    }
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                    sx={{
                      background: 'white',
                      backdropFilter: 'blur(10px)',
                      minHeight: 56,
                      '& .MuiAccordionSummary-content': { alignItems: 'center', my: 2 },
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.04)
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                      <BusinessIcon sx={{ color: theme.palette.primary.main }} />
                      <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                        Overhead Costs
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                        {formatCurrency(costs.overheadCost)} ({globalOverheadPercentage.toFixed(1)}%)
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2 }}>
                    {/* Global Overhead Percentage */}
                    <Box sx={{ 
                      mb: 3, 
                      p: 2, 
                      borderRadius: 3,
                      border: `1px solid ${theme.palette.grey[400]}`,
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                      }
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 2, color: '#000000' }}>
                        Total Overhead as % of Material Costs
                      </Typography>
                      <Box sx={{ px: 1, mb: 2 }}>
                        <Slider
                          value={globalOverheadPercentage}
                          onChange={(e, newValue) => canEdit ? setGlobalOverheadPercentage(newValue) : null}
                          disabled={!canEdit}
                          valueLabelDisplay="auto"
                          step={1}
                          min={10}
                          max={60}
                          marks={[
                            { value: 10, label: '10%' },
                            { value: 30, label: '30%' },
                            { value: 50, label: '50%' },
                            { value: 60, label: '60%' }
                          ]}
                          sx={{
                            '& .MuiSlider-thumb': {
                              backgroundColor: theme.palette.primary.main
                            },
                            '& .MuiSlider-track': {
                              backgroundColor: theme.palette.primary.main
                            },
                            '& .MuiSlider-markLabel': {
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              color: 'text.primary',
                              '&[data-index="0"]': {
                                transform: 'translateX(0%)'
                              },
                              '&[data-index="3"]': {
                                transform: 'translateX(-100%)'
                              }
                            }
                          }}
                        />
                      </Box>
                      <TextField
                        size="small"
                        label="Global Overhead Percentage"
                        type="number"
                        value={globalOverheadPercentage}
                        onChange={(e) => {
                          if (!canEdit) return;
                          const val = parseFloat(e.target.value);
                          if (!isNaN(val) && val >= 10 && val <= 60) {
                            setGlobalOverheadPercentage(val);
                          } else if (e.target.value === '') {
                            setGlobalOverheadPercentage(10);
                          }
                        }}
                        disabled={!canEdit}
                        inputProps={{ min: 10, max: 60, step: 1 }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">%</InputAdornment>
                        }}
                        sx={{ 
                          maxWidth: 200,
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { 
                              borderColor: alpha(theme.palette.grey[400], 0.8), 
                              borderWidth: 1,
                              borderRadius: 2
                            },
                            '&:hover fieldset': { 
                              borderColor: alpha(theme.palette.primary.main, 0.4), 
                              borderWidth: 1 
                            },
                            '&.Mui-focused fieldset': { 
                              borderColor: theme.palette.primary.main, 
                              borderWidth: 1 
                            }
                          }
                        }}
                      />
                    </Box>
                    
                    {/* Enhanced Sticky Summary */}
                    <Box sx={{ 
                      p: 2,
                      borderRadius: 3,
                      border: `1px solid ${distributionTotal > 100 ? '#f44336' : theme.palette.grey[400]}`,
                      background: distributionTotal > 100 ? 
                        `linear-gradient(${alpha('#f44336', 0.05)}, ${alpha('#f44336', 0.02)})` :
                        `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(10px)',
                      mb: 2,
                      position: 'sticky',
                      top: 10,
                      zIndex: 10,
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                      }
                    }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={3}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Global Overhead
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                            {globalOverheadPercentage}%
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={3}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Distribution Used
                          </Typography>
                          <Typography variant="body1" sx={{ 
                            fontWeight: 500, 
                            color: distributionTotal > 100 ? '#f44336' : '#000000' 
                          }}>
                            {distributionTotal}%
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={3}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Overhead Cost
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                            {formatCurrency(costs.overheadCost)}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={3}>
                          {distributionTotal > 100 ? (
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              onClick={() => {
                                if (!canEdit) {
                                  showSnackbar('You do not have permission to modify overhead distribution.', 'error');
                                  return;
                                }
                                setUtilitiesPercentage(10);
                                setFacilityPercentage(25);
                                setEquipmentPercentage(15);
                                setAdminLaborPercentage(18);
                                setWarehousePercentage(7);
                                setQualityPercentage(10);
                                setSoftwarePercentage(8);
                                setInsurancePercentage(7);
                              }}
                              disabled={!canEdit}
                              sx={{ 
                                fontSize: '0.75rem', 
                                py: 0.5, 
                                px: 1,
                                borderRadius: 2,
                                fontWeight: 500
                              }}
                            >
                              Fix Distribution
                            </Button>
                          ) : (
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Status
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#2e7d32' }}>
                                ✓ Valid
                              </Typography>
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    </Box>
                    
                    {/* Simple List Layout */}
                    <Stack spacing={1}>
                      {[
                        { label: 'Utilities', value: utilitiesPercentage, setter: setUtilitiesPercentage, color: '#2196f3' },
                        { label: 'Facility', value: facilityPercentage, setter: setFacilityPercentage, color: '#4caf50' },
                        { label: 'Equipment', value: equipmentPercentage, setter: setEquipmentPercentage, color: '#ff9800' },
                        { label: 'Admin Labor', value: adminLaborPercentage, setter: setAdminLaborPercentage, color: '#9c27b0' },
                        { label: 'Warehouse', value: warehousePercentage, setter: setWarehousePercentage, color: '#607d8b' },
                        { label: 'Quality', value: qualityPercentage, setter: setQualityPercentage, color: '#e91e63' },
                        { label: 'Software', value: softwarePercentage, setter: setSoftwarePercentage, color: '#795548' },
                        { label: 'Insurance', value: insurancePercentage, setter: setInsurancePercentage, color: '#f44336' }
                      ].map((item, index) => {
                         const actualPercentage = (item.value / 100) * globalOverheadPercentage;
                         
                         return (
                           <Box key={index} sx={{ 
                             p: 2,
                             borderRadius: 3,
                             border: `1px solid ${theme.palette.grey[400]}`,
                             background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                             backdropFilter: 'blur(10px)',
                             transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                             '&:hover': {
                               transform: 'translateY(-2px)',
                               boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                             }
                           }}>
                             <Grid container spacing={2} alignItems="center">
                               <Grid item xs={3}>
                                 <Typography variant="body2" sx={{ 
                                   fontWeight: 500, 
                                   color: item.color
                                 }}>
                                   {item.label}
                                 </Typography>
                                 <Typography variant="caption" color="text.secondary">
                                   = {actualPercentage.toFixed(1)}% total
                                 </Typography>
                               </Grid>
                               
                               <Grid item xs={1}>
                                 <Typography variant="h6" sx={{ 
                                   fontWeight: 700, 
                                   color: item.color,
                                   textAlign: 'center'
                                 }}>
                                   {item.value}%
                                 </Typography>
                               </Grid>
                               
                               <Grid item xs={8}>
                                 <Slider
                                   value={item.value}
                                   onChange={(e, newValue) => {
                                     if (!canEdit) return;
                                     // Ensure total doesn't exceed 100%
                                     const otherTotal = distributionTotal - item.value;
                                     const maxValue = Math.min(100 - otherTotal, newValue);
                                     item.setter(Math.max(0, maxValue));
                                   }}
                                   disabled={!canEdit}
                                   step={1}
                                   min={0}
                                   max={100}
                                   size="small"
                                   sx={{
                                     '& .MuiSlider-thumb': {
                                       backgroundColor: item.color
                                     },
                                     '& .MuiSlider-track': {
                                       backgroundColor: item.color
                                     }
                                   }}
                                 />
                               </Grid>
                             </Grid>
                           </Box>
                         );
                       })}
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                {/* Additional Costs */}
                <Accordion 
                  expanded={accordionStates.additionalCosts}
                  onChange={(event, expanded) => updateAccordionState('additionalCosts', expanded)}
                  elevation={0}
                  sx={{
                    borderRadius: '12px !important',
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(20px)',
                    overflow: 'hidden',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                    '&:before': {
                      display: 'none'
                    },
                    '&.Mui-expanded': {
                      margin: '8px 0 8px 0',
                      borderRadius: '12px !important'
                    },
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '& .MuiAccordion-region': {
                      borderRadius: '0 0 12px 12px !important'
                    }
                  }}
                >
                  <AccordionSummary 
                    expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.primary.main }} />}
                    sx={{
                      background: 'white',
                      backdropFilter: 'blur(10px)',
                      minHeight: 56,
                      '& .MuiAccordionSummary-content': { alignItems: 'center', my: 2 },
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.04)
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                      <SupplierIcon sx={{ color: theme.palette.primary.main }} />
                      <Typography variant="body1" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                        Additional Costs
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                        {formatCurrency(costs.additionalCost)}
                      </Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 2 }}>
                    <Grid container spacing={2}>
                      {[
                        { label: 'Shipping', value: shippingCost, setter: setShippingCost },
                        { label: 'Waste', value: wasteCost, setter: setWasteCost },
                        { label: 'Rework', value: reworkCost, setter: setReworkCost },
                        { label: 'Misc', value: miscCost, setter: setMiscCost }
                      ].map((item, index) => (
                        <Grid item xs={6} key={index}>
                          <TextField
                            fullWidth
                            size="small"
                            label={item.label}
                            type="number"
                            value={item.value}
                            onChange={(e) => canEdit ? item.setter(Math.max(parseFloat(e.target.value) || 0, 0)) : null}
                            disabled={!canEdit}
                            InputProps={{
                              startAdornment: <InputAdornment position="start">$</InputAdornment>
                            }}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: 'white',
                                '& fieldset': { 
                                  borderColor: alpha(theme.palette.grey[400], 0.8), 
                                  borderWidth: 1,
                                  borderRadius: 2
                                },
                                '&:hover fieldset': { 
                                  borderColor: alpha(theme.palette.primary.main, 0.4), 
                                  borderWidth: 1 
                                },
                                '&.Mui-focused fieldset': { 
                                  borderColor: theme.palette.primary.main, 
                                  borderWidth: 1 
                                }
                              }
                            }}
                          />
                        </Grid>
                      ))}
                      
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        {/* Panel C: Cost Breakdown & Batch Info */}
        <Grid item xs={12} lg={3}>
          <Stack spacing={3}>
            {/* Cost Breakdown Card */}
            <Card sx={{ 
              borderRadius: 4,
              border: `1px solid ${theme.palette.grey[400]}`,
              background: `white`,
              backdropFilter: 'blur(20px)',
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
            }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 3 }}>
                  Cost Breakdown
                </Typography>

                <Stack spacing={2}>
                  {/* Materials Cost */}
                  <Box sx={{ 
                    p: 2,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                    }
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                        Materials Cost
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                        {formatCurrency(costs.totalMaterialCost)}
                      </Typography>
                    </Stack>
                  </Box>

                  {/* Marketing Cost */}
                  <Box sx={{ 
                    p: 2,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                    }
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                          Marketing Cost
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          (${marketingCostPerUnit} × {calculateTotalUnitsFromLineProduction().toLocaleString()} units)
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                        {formatCurrency(marketingCostPerUnit * calculateTotalUnitsFromLineProduction())}
                      </Typography>
                    </Stack>
                  </Box>

                  {/* Labor Cost */}
                  <Box sx={{ 
                    p: 2,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                    }
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                          Labor Cost
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({laborPercentage}% of materials)
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                        {formatCurrency(costs.laborCost)}
                      </Typography>
                    </Stack>
                  </Box>

                  {/* Overhead Cost */}
                  <Box sx={{ 
                    p: 2,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                    }
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                          Overhead Cost
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ({globalOverheadPercentage}% of materials)
                        </Typography>
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                        {formatCurrency(costs.overheadCost)}
                      </Typography>
                    </Stack>
                  </Box>

                  {/* Additional Costs - only show if any value > 0 */}
                  {(shippingCost > 0 || wasteCost > 0 || reworkCost > 0 || miscCost > 0) && (
                    <Box sx={{ 
                      p: 2,
                      borderRadius: 3,
                      border: `1px solid ${theme.palette.grey[400]}`,
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                      }
                    }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500, color: '#000000' }}>
                            Additional Costs
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {[
                              shippingCost > 0 && `Shipping: ${formatCurrency(shippingCost)}`,
                              wasteCost > 0 && `Waste: ${formatCurrency(wasteCost)}`,
                              reworkCost > 0 && `Rework: ${formatCurrency(reworkCost)}`,
                              miscCost > 0 && `Misc: ${formatCurrency(miscCost)}`
                            ].filter(Boolean).join(', ')}
                          </Typography>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000' }}>
                          {formatCurrency(costs.additionalCost)}
                        </Typography>
                      </Stack>
                    </Box>
                  )}

                  <Divider />

                  {/* Total Cost */}
                  <Box sx={{ 
                    p: 3,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.primary.main}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                    backdropFilter: 'blur(10px)',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                    }
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                        Total
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                        {formatCurrency(costs.totalCost)}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </CardContent>
            </Card>


            {/* Batch Info Card */}
            {(lineProduction?.batch_id || linkedBatch) && (
              <Card sx={{ 
                borderRadius: 4,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: `white`,
                backdropFilter: 'blur(20px)',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, color: '#000000', mb: 3 }}>
                    Batch Info
                  </Typography>

                  <Box sx={{ 
                    p: 3,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                    backdropFilter: 'blur(10px)',
                    textAlign: 'center',
                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 8px 24px ${alpha('#000000', 0.15)}`
                    }
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 1 }}>
                      LINE PRODUCTION UNITS
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 500, color: '#000000' }}>
                      {calculateTotalUnitsFromLineProduction().toLocaleString()} units
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ProductionLineCosts;